/**
 * A receipt photo, made into what is uploaded, on the phone: a compressed copy of the photo (the
 * evidence, upright, at most 2400 px, without its EXIF data) and the scan - the receipt alone,
 * found by its edges, straightened and in black and white. A long receipt on a table is mostly
 * table; only the paper goes into the scan.
 *
 * The browser half (canvas decode and encode); the pixel work is lib/scan/geometry.ts and
 * binarize.ts, which services/ai shares for emailed receipts and re-scans. Not unit tested
 * (jsdom has no canvas): e2e/capture.spec.ts covers it in Chromium and WebKit.
 */
import type { ScanCorner, ScanPage } from "@/lib/api/documents";
import { binarize } from "./binarize";
import { findReceipt } from "./edges";
import { scaleQuad, sharpness, targetSize, warpGray, type Quad } from "./geometry";
import { jpegPdf } from "./pdf";

/** Longest side of the stored photo: enough to re-crop it and read the RKSV code. */
export const ORIGINAL_SIDE = 2400;
const ORIGINAL_QUALITY = 0.8;
const SCAN_QUALITY = 0.8;
/** The size the edges are looked for at, as services/ai does. */
const DETECT_SIDE = 640;
/** Sharpness is measured on a copy this wide, so the numbers match services/ai's thresholds. */
const SHARPNESS_WIDTH = 800;

export type ScannedPhoto = {
  /** The compressed photo, upright. */
  original: File;
  /** The scan of this page as a black and white JPEG. */
  scan: File;
  page: ScanPage;
};

type Gray = { data: Uint8Array; width: number; height: number };

const fit = (width: number, height: number, side: number) => {
  const factor = Math.min(1, side / Math.max(width, height));
  return { width: Math.round(width * factor), height: Math.round(height * factor) };
};

function canvas(width: number, height: number) {
  const element = document.createElement("canvas");
  element.width = width;
  element.height = height;
  const context = element.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("No canvas");
  context.imageSmoothingQuality = "high";
  return { element, context };
}

/** Frees a canvas's pixels at once: iOS counts canvas memory across the page and runs out. */
const release = (element: HTMLCanvasElement) => {
  element.width = 0;
  element.height = 0;
};

const jpeg = (element: HTMLCanvasElement, quality: number) =>
  new Promise<Blob>((resolve, reject) =>
    element.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("The photo could not be encoded"))),
      "image/jpeg",
      quality,
    ),
  );

/** An <img> applies the EXIF orientation, so what is drawn is upright as the person saw it. */
async function decode(file: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.src = url;
  try {
    await image.decode();
  } finally {
    URL.revokeObjectURL(url);
  }
  return image;
}

function grayOf(rgba: Uint8ClampedArray, width: number, height: number): Gray {
  const data = new Uint8Array(width * height);
  for (let i = 0; i < data.length; i += 1) {
    const o = i * 4;
    data[i] = Math.round(0.299 * rgba[o]! + 0.587 * rgba[o + 1]! + 0.114 * rgba[o + 2]!);
  }
  return { data, width, height };
}

function grayCanvas(page: Gray) {
  const { element, context } = canvas(page.width, page.height);
  const image = context.createImageData(page.width, page.height);
  for (let i = 0; i < page.data.length; i += 1) {
    const o = i * 4;
    image.data[o] = image.data[o + 1] = image.data[o + 2] = page.data[i]!;
    image.data[o + 3] = 255;
  }
  context.putImageData(image, 0, 0);
  return element;
}

function measureSharpness(page: Gray): number {
  const source = grayCanvas(page);
  const factor = Math.min(1, SHARPNESS_WIDTH / page.width);
  const width = Math.max(1, Math.round(page.width * factor));
  const height = Math.max(1, Math.round(page.height * factor));
  const { element, context } = canvas(width, height);
  context.drawImage(source, 0, 0, width, height);
  const small = grayOf(context.getImageData(0, 0, width, height).data, width, height);
  release(source);
  release(element);
  return Math.round(sharpness(small));
}

/** The receipt cut out along `corners` (null: the whole photo), black and white. */
async function cut(
  original: File,
  gray: Gray,
  corners: Quad | null,
  detected: boolean,
): Promise<ScannedPhoto> {
  let page: Gray = gray;
  if (corners) {
    const target = targetSize(corners);
    page = { data: warpGray(gray, corners, target.width, target.height), ...target };
  }
  const bw = grayCanvas({ ...page, data: binarize(page) });
  const scan = new File(
    [await jpeg(bw, SCAN_QUALITY)],
    original.name.replace(/\.[^.]+$/, "-scan.jpg"),
    { type: "image/jpeg" },
  );
  release(bw);
  return {
    original,
    scan,
    page: {
      corners,
      detected,
      width: page.width,
      height: page.height,
      sourceWidth: gray.width,
      sourceHeight: gray.height,
      sharpness: measureSharpness(page),
    },
  };
}

/**
 * One photo to its compressed copy and its scan page, the edges found automatically. Throws when
 * the browser cannot decode it (a HEIC on a desktop Chrome): the caller then uploads it as it is.
 */
export async function scanPhoto(file: Blob, name: string): Promise<ScannedPhoto> {
  const image = await decode(file);
  const size = fit(image.naturalWidth, image.naturalHeight, ORIGINAL_SIDE);
  const photo = canvas(size.width, size.height);
  photo.context.drawImage(image, 0, 0, size.width, size.height);
  const original = new File([await jpeg(photo.element, ORIGINAL_QUALITY)], name, {
    type: "image/jpeg",
  });
  const gray = grayOf(
    photo.context.getImageData(0, 0, size.width, size.height).data,
    size.width,
    size.height,
  );
  release(photo.element);

  // The edges, found on a small colour copy like services/ai does, then scaled to the photo.
  const small = fit(size.width, size.height, DETECT_SIDE);
  const probe = canvas(small.width, small.height);
  probe.context.drawImage(image, 0, 0, small.width, small.height);
  const found = findReceipt({
    data: new Uint8Array(probe.context.getImageData(0, 0, small.width, small.height).data.buffer),
    width: small.width,
    height: small.height,
    channels: 4,
  });
  release(probe.element);
  // No edges found: the whole photo is kept - a missed crop costs looks, a wrong one data.
  const corners = found ? roundQuad(scaleQuad(found.corners, size.width / small.width)) : null;
  return cut(original, gray, corners, corners !== null);
}

/** The same photo cut again along corners a person set (null: the whole photo). */
export async function recutPhoto(
  photo: ScannedPhoto,
  corners: ScanCorner[] | null,
): Promise<ScannedPhoto> {
  // The compressed copy is upright and at most ORIGINAL_SIDE, the space the corners are in.
  const image = await decode(photo.original);
  const { naturalWidth: width, naturalHeight: height } = image;
  const drawn = canvas(width, height);
  drawn.context.drawImage(image, 0, 0);
  const gray = grayOf(drawn.context.getImageData(0, 0, width, height).data, width, height);
  release(drawn.element);
  return cut(photo.original, gray, corners ? (corners as Quad) : null, false);
}

/** Whole pixels: what a person moves and what is stored. */
const roundQuad = (quad: Quad): Quad =>
  quad.map((point) => ({ x: Math.round(point.x), y: Math.round(point.y) })) as Quad;

/** The scan pages as one PDF, the document's main file, named like the receipt. */
export async function scanPdf(photos: ScannedPhoto[], name: string): Promise<File> {
  const pages = await Promise.all(
    photos.map(async (photo) => ({
      jpeg: new Uint8Array(await photo.scan.arrayBuffer()),
      width: photo.page.width,
      height: photo.page.height,
      // A canvas writes colour JPEGs, even of a grey page.
      gray: false,
    })),
  );
  return new File([jpegPdf(pages)], name.replace(/\.[^.]+$/, "") + ".pdf", {
    type: "application/pdf",
  });
}
